import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Api from "../../../../api";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from '@material-ui/icons/Close';
import { SimpleModal } from "components/Modal";
import { CircularProgress, MenuItem } from "@material-ui/core";
import LeftHandedModal from "../../../landing/SignUp2_Talent/components/LeftHandedModal";
import { MaterialSelector } from "../../../../components/Selector";
import { HygienistSkill, roles } from "utils/constants";
import { HygienistProps, UserAccountProps } from "../../../../utils/types";
import { getRoleByTypeNumber, getTypeByRoleNumber } from "../../../../utils";
import Languages from "components/Languages";
import { Language } from "api/Api";
import { buildHygienistSkillPayload, QualificationSkillLike } from "./qualificationSkills";
import { saveQualification } from "./qualificationSave";
import styles from "./components.module.scss";
import clsx from "clsx";
import MaterialInput from 'components/Inputs/MaterialInput';

interface QualificationProps {
    hygienist: {
        skills: HygienistSkill[],
    } & HygienistProps,
    isOpen: boolean,
    onClose: () => void,
    onSaveSuccess?: (user: UserAccountProps) => void | Promise<void>,
    hideCloseButton?: boolean,
    autoScrollLanguages?: boolean,
}

export const Qualification: FC<QualificationProps> = ({
    hygienist,
    isOpen,
    onClose,
    onSaveSuccess,
    hideCloseButton = false,
    autoScrollLanguages = false,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const savingRef = useRef(false);
    const [ready, setReady] = useState(false);
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [needsCdhoRepair, setNeedsCdhoRepair] = useState(false);
    const [cdhoNumber, setCdhoNumber] = useState('');
    const formGeneration = useRef(0);
    // Invalidate synchronously on route/unmount changes, before an already
    // queued network continuation can perform another write or navigate.
    useLayoutEffect(() => () => { formGeneration.current += 1; }, [isOpen, hygienist.id]);

    const hygienistLicenseQualification = !!hygienist?.skills.find(skill => skill.license && skill.qualification)

    const province = hygienist?.province

    const roleNumber = getRoleByTypeNumber(hygienist?.type);
    const isHygienistOrAssistant = roleNumber === roles.HYGIENIST || roleNumber === roles.ASSISTANT
    const [skills, setSkills] = useState<HygienistSkill[]>([]);

    const [userQualification, setUserQualification] = useState<string | undefined>('');
    const [checkedSkills, setCheckedSkills] = useState<QualificationSkillLike[]>(hygienist.skills);
    const [licensed, setLicensed] = useState<boolean | undefined>(
        hideCloseButton && !hygienistLicenseQualification
            ? undefined
            : (isHygienistOrAssistant ? !hygienistLicenseQualification : true)
    );
    const [isLeftHanded, setIsLeftHanded] = useState<boolean>(!!hygienist?.isLeftHanded);
    const [isLeftHandedModal, setIsLeftHandedModal] = useState<boolean>(false);

    // Language state
    const [initiallySelectedLanguages, setInitiallySelectedLanguages] = useState<Language[]>([]);
    const [selectedLangIds, setSelectedLangIds] = useState<number[]>([]);
    const [regionalLanguages, setRegionalLanguages] = useState<{ common: Language[], additional: Language[] }>({ common: [], additional: [] });

    const [errorMessage, setErrorMessage] = useState<any>({
        license: '',
        education: '',
        graduationYear: '',
        about: '',
    });

    useEffect(() => {
        const generation = ++formGeneration.current;
        savingRef.current = false;
        setLoading(false);
        setReady(false);
        if (!isOpen) return;
        let cancelled = false;
        setLoadError('');
        setSaveError('');
        setNeedsCdhoRepair(false);
        setCdhoNumber('');
        void (async () => {
            try {
                const [catalog, languages, myLanguages, regional, user] = await Promise.all([
                    isHygienistOrAssistant
                        ? Api.getSkills({ type: getTypeByRoleNumber(roleNumber), state: province })
                        : Promise.resolve([]),
                    Api.getLanguages(), Api.getMyLanguages(), Api.getRegionalLanguages(),
                    Api.getUser(),
                ]);
                if (cancelled) return;
                const initialHygienist = user?.hygienist;
                if (!initialHygienist || initialHygienist.id !== hygienist.id) throw new Error('Qualification account changed');
                setNeedsCdhoRepair(Number(initialHygienist.type) === 0
                    && ['ON', 'ONTARIO'].includes(String(initialHygienist.province || '').trim().toUpperCase())
                    && !String(initialHygienist.CDHONumber ?? '').trim());
                const initialSkills = initialHygienist.skills || [];
                // Do not mount Languages with an empty baseline while its saved
                // selection is still loading; a fast Save could remove it.
                setInitiallySelectedLanguages(myLanguages.map(language =>
                    languages.find(item => item.id === language.id) || language));
                setSelectedLangIds(myLanguages.map(language => language.id));
                setRegionalLanguages(regional);
                setSkills(catalog);
                setCheckedSkills(initialSkills);
                const hasUnlicensedQualification = initialSkills.some(skill => skill.license && skill.qualification);
                setLicensed(hideCloseButton && !hasUnlicensedQualification
                    ? undefined : (isHygienistOrAssistant ? !hasUnlicensedQualification : true));
                setIsLeftHanded(!!initialHygienist.isLeftHanded);
                setUserQualification(catalog.find(skill => skill.qualification && !skill.license
                    && initialSkills.some(saved => saved.id === skill.id))?.name);
                setReady(true);
            } catch {
                if (!cancelled) setLoadError('We could not load your qualifications and languages. Please try again.');
            }
        })();
        return () => {
            cancelled = true;
            if (formGeneration.current === generation) formGeneration.current += 1;
        };
    }, [isOpen, hygienist.id, roleNumber, province, isHygienistOrAssistant, hideCloseButton, loadAttempt]);

    useEffect(() => {
        if (licensed === false) {
            setUserQualification('');
        }
    }, [licensed]);

    // Auto-scroll to languages section if requested
    const hasScrolledRef = useRef<boolean>(false);
    useEffect(() => {
        if (!isOpen) hasScrolledRef.current = false;
        if (isOpen && ready && autoScrollLanguages && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            setTimeout(() => {
                document.getElementById('languages-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        }
    }, [isOpen, ready, autoScrollLanguages]);
    let label = '';
    let labelDescription = '';
    let noLicenseLabel = '';
    let noLicenselabelDescription = '';

    const licenseSkill = skills.find(skill => skill.license && !skill.qualification)
    const qualificationSkills = skills.filter((skill: HygienistSkill) => skill.qualification && !skill.license);
    const licenseQualificationSkill = skills.find(skill => skill.license && skill.qualification)
    if (licenseSkill) {
        label = licenseSkill.name === 'RDH'
            ? 'I am an Actively Licensed Registered Dental Hygienist (RDH)'
            : 'I am a Certified or Registered Dental Assistant';
        labelDescription = licenseSkill.name === 'RDH'
            ? `This means you are formally trained, qualified and have an active RDH license in good standing with your regulatory body and can legally work as a Dental Hygienist in ${province}.`
            : `This means you have received formal training as a Dental Assistant and have an active Certification or Registration in good standing in ${province}.`;
    }
    if (licenseQualificationSkill) {
        noLicenseLabel = `I am not a Certified or Registered Dental Assistant`
        noLicenselabelDescription = `The means that you have been trained on-the-job to a high level of competency and professionalism, and are skilled and qualified to work as a dental assistant in ${province}`
    }

    const commonLanguagesList = regionalLanguages.common;
    const additionalLanguagesList = regionalLanguages.additional;

    const onSubmitSkills = async (event?: React.FormEvent) => {
        event?.preventDefault()
        if (!ready || savingRef.current) return;
        let errors = {}
        setErrorMessage(errors as any);

        if (licensed || !licenseQualificationSkill) {
            if (!licensed && roles.HYGIENIST === roleNumber) {
                errors = { ...errors, licensed: 'Please confirm your qualification' };
            }
        }
        if (licensed === undefined) {
            errors = { ...errors, licensed: 'Please confirm your qualification' };
        }

        if (licensed && (roleNumber === roles.ASSISTANT || roleNumber === roles.HYGIENIST)) {
            if (!userQualification && qualificationSkills.length > 0) {
                errors = { ...errors, qualification: 'Please select your qualification level' };
            }
        }

        if (Object.values(errors).find((err: any) => !!err.length)) {
            setErrorMessage(errors as any);
            return console.log('ERROR', errors)
        }

        savingRef.current = true;
        const generation = formGeneration.current;
        const isCurrent = () => formGeneration.current === generation;
        setLoading(true);
        setSaveError('');
        try {
            const checkedSkillIds = checkedSkills.map(skill => skill.id)
            const qualificationSkill = userQualification
                ? skills.find((skill: HygienistSkill) => skill.name === userQualification)
                : undefined;

            // T2-6561: ensure at most one qualification-level skill in the replace
            // payload (see buildHygienistSkillPayload for details).
            const arraySkillIds = buildHygienistSkillPayload({
                checkedSkillIds,
                qualificationSkills,
                licenseSkill,
                licenseQualificationSkill,
                selectedQualificationSkill: qualificationSkill,
                licensed,
            });
            const user = await saveQualification({
                hygienistId: hygienist.id,
                skillIds: isHygienistOrAssistant ? arraySkillIds : undefined,
                qualificationLevel: isHygienistOrAssistant ? (licensed && userQualification === 'CDA2' ? 2 : 1) : undefined,
                languageIds: [...selectedLangIds],
                isLeftHanded: hygienist.type === 0 ? isLeftHanded : undefined,
                cdhoNumber: needsCdhoRepair && cdhoNumber.trim() ? cdhoNumber.trim() : undefined,
                isCurrent,
            });
            if (!isCurrent()) return;
            if (onSaveSuccess) await onSaveSuccess(user);
            else onClose();
        } catch {
            if (isCurrent()) setSaveError('We could not confirm that all your changes were saved. Some changes may already be saved. Your selections are still here; please try Save again.');
        } finally {
            if (isCurrent()) {
                savingRef.current = false;
                setLoading(false);
            }
        }
    }

    const qualificationHandler = (e: any) => {
        setUserQualification(e.target.value)
        if (e.target.value) {
            setErrorMessage({ ...errorMessage, qualification: '' })
        }
    }

    const handleSkillToggle = (skill: QualificationSkillLike) => {
        const isChecked = checkedSkills.some(s => s.id === skill.id);
        if (isChecked) {
            setCheckedSkills(checkedSkills.filter(s => s.id !== skill.id));
        } else {
            setCheckedSkills([...checkedSkills, skill]);
        }
    };

    // Filter out license and qualification skills from additional skills
    const additionalSkillsList = skills.filter(
        skill => !skill.license && !skill.qualification
    ) as Array<{ id: number; name: string; label: string; license: boolean; qualification: boolean }>;

    if (!isOpen) return null;

    return (
        <>
            {/* Main Modal */}
            <div className={styles.wizardModalOverlay} onClick={hideCloseButton || loading ? undefined : onClose}>
                <div className={styles.wizardModalContainer} role="dialog" aria-modal="true" aria-label="Qualification and skills" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className={styles.wizardModalHeader}>
                        <div className={styles.wizardHeaderContent}>
                            <h1 className={styles.wizardModalTitle}>Qualification & Skills</h1>
                            <p className={styles.wizardModalSubtitle}>
                                {hideCloseButton
                                    ? 'Please confirm your qualification to continue'
                                    : 'Update your professional qualifications and skills'}
                            </p>
                        </div>
                        {!hideCloseButton && (
                            <button className={styles.wizardCloseButton} onClick={onClose} disabled={loading} aria-label="Close qualifications" data-testid="qualification-close-button">
                                <CloseIcon />
                            </button>
                        )}
                    </div>

                    {/* Body */}
                    <form className={styles.wizardModalBody} onSubmit={onSubmitSkills} aria-busy={!ready || loading}>
                        {!ready && !loadError && <p role="status">Loading qualifications and languages…</p>}
                        {loadError && <div role="alert" data-testid="qualification-load-error">
                            <p>{loadError}</p>
                            <button type="button" data-testid="qualification-retry-load-button" onClick={() => setLoadAttempt(attempt => attempt + 1)}>Try again</button>
                        </div>}
                        {ready && <fieldset disabled={loading} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
                        {/* License/Qualification Section */}
                        {licenseQualificationSkill ? (
                            <div className={styles.wizardFormSection}>
                                <h3 className={styles.wizardSectionTitle}>Your Qualification</h3>
                                <div className={styles.wizardCheckboxGroup}>
                                    <div
                                        className={clsx(styles.wizardRadioOption, { [styles.selected]: licensed === true })}
                                        onClick={() => { if (!loading) setLicensed(true); }}
                                    >
                                        <input
                                            type="radio"
                                            name="license"
                                            aria-label={label}
                                            data-testid="qualification-licensed-radio"
                                            checked={licensed === true}
                                            onChange={() => setLicensed(true)}
                                        />
                                        <div className={styles.radioContent}>
                                            <div className={styles.radioLabel} style={{ color: errorMessage?.licensed ? '#f44336' : '' }}>
                                                {label}
                                            </div>
                                            <div className={styles.radioDescription}>{labelDescription}</div>
                                        </div>
                                    </div>
                                    <div
                                        className={clsx(styles.wizardRadioOption, { [styles.selected]: licensed === false })}
                                        onClick={() => { if (!loading) setLicensed(false); }}
                                    >
                                        <input
                                            type="radio"
                                            name="license"
                                            aria-label={noLicenseLabel}
                                            data-testid="qualification-unlicensed-radio"
                                            checked={licensed === false}
                                            onChange={() => setLicensed(false)}
                                        />
                                        <div className={styles.radioContent}>
                                            <div className={styles.radioLabel}>{noLicenseLabel}</div>
                                            <div className={styles.radioDescription}>{noLicenselabelDescription}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : licenseSkill && (
                            <div className={styles.wizardFormSection}>
                                <h3 className={styles.wizardSectionTitle}>Your Qualification</h3>
                                <div
                                    className={clsx(styles.wizardRadioOption, { [styles.selected]: licensed === true })}
                                    onClick={hideCloseButton && !loading ? () => setLicensed(prev => !prev) : undefined}
                                    style={hideCloseButton ? { cursor: 'pointer' } : undefined}
                                >
                                    <input
                                        type="checkbox"
                                        name={licenseSkill?.name || ''}
                                        aria-label={label}
                                        data-testid="qualification-license-checkbox"
                                        checked={licensed === true}
                                        disabled={!hideCloseButton}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={hideCloseButton ? () => setLicensed(prev => !prev) : () => {}}
                                    />
                                    <div className={styles.radioContent}>
                                        <div className={styles.radioLabel} style={{ color: errorMessage?.licensed ? '#f44336' : '' }}>
                                            {label}
                                        </div>
                                        <div className={styles.radioDescription}>{labelDescription}</div>
                                    </div>
                                </div>
                                {errorMessage?.licensed && (
                                    <p style={{ color: '#f44336', fontSize: 13, margin: '8px 0 0', paddingLeft: 4 }}>
                                        {errorMessage.licensed}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Qualification Level Dropdown */}
                        {!!qualificationSkills.length && (
                            <div className={styles.wizardInputContainer}>
                                <MaterialSelector
                                    key={`qualification-${userQualification || 'empty'}`}
                                    disabled={loading || licensed === false}
                                    id={'qualification'}
                                    name={'qualification'}
                                    label={'Qualification Level'}
                                    errorMessage={errorMessage?.qualification}
                                    onChange={qualificationHandler}
                                    value={userQualification}
                                >
                                    {qualificationSkills.map((skill: HygienistSkill) => (
                                        <MenuItem key={skill.id} value={skill.name} id={skill.name}>
                                            {skill.label}
                                        </MenuItem>
                                    ))}
                                </MaterialSelector>
                            </div>
                        )}

                        {needsCdhoRepair && <div className={styles.wizardInputContainer}>
                            <MaterialInput
                                id="qualification-cdho-number"
                                name="CDHONumber"
                                label="Hygiene Licence # (CDHO)"
                                value={cdhoNumber}
                                onChange={(event) => setCdhoNumber(event.target.value)}
                                inputProps={{ inputProps: { maxLength: 20, 'data-testid': 'qualification-cdho-input' } }}
                                helperText="Your CDHO number is missing. Add it here to complete your professional details."
                            />
                        </div>}

                        {/* Left-handed checkbox for Hygienists */}
                        {roleNumber === roles.HYGIENIST && (
                            <div className={styles.wizardFormSection}>
                                <div
                                    className={clsx(styles.wizardRadioOption)}
                                    onClick={() => { if (!loading) setIsLeftHanded(!isLeftHanded); }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <input
                                        type="checkbox"
                                        name="isLeftHanded"
                                        aria-label="I'm left-handed"
                                        data-testid="qualification-left-handed-checkbox"
                                        checked={isLeftHanded}
                                        onClick={(event) => event.stopPropagation()}
                                        onChange={(e) => setIsLeftHanded(e.target.checked)}
                                    />
                                    <div className={styles.radioContent}>
                                        <div className={styles.radioLabel}>I'm left-handed</div>
                                    </div>
                                    <InfoOutlinedIcon
                                        style={{ color: '#6B7280', cursor: 'pointer' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsLeftHandedModal(true);
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Languages Section */}
                        <div id="languages-section" className={styles.wizardLanguagesSection}>
                            <h3 className={styles.wizardSectionTitle}>Languages</h3>
                            <Languages
                                commonLanguages={commonLanguagesList}
                                otherLanguages={additionalLanguagesList}
                                initialSelected={initiallySelectedLanguages}
                                onChange={setSelectedLangIds}
                            />
                        </div>

                        {/* Additional Skills */}
                        {additionalSkillsList.length > 0 && (
                            <div className={styles.wizardSkillsContainer}>
                                <h3 className={styles.wizardSectionTitle}>Additional Skills</h3>
                                <div className={styles.wizardSkillsChips}>
                                    {additionalSkillsList.map((skill) => (
                                        <button
                                            key={skill.id}
                                            type="button"
                                            data-testid={`qualification-additional-skill-${skill.id}`}
                                            aria-pressed={checkedSkills.some(s => s.id === skill.id)}
                                            className={clsx(styles.wizardSkillChip, {
                                                [styles.selected]: checkedSkills.some(s => s.id === skill.id),
                                            })}
                                            onClick={() => handleSkillToggle(skill as HygienistSkill)}
                                        >
                                            {skill.label || skill.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        </fieldset>}
                    </form>
                    {saveError && <div role="alert" data-testid="qualification-save-error" style={{ padding: '12px 24px' }}>{saveError}</div>}

                    {/* Footer */}
                    <div className={styles.wizardModalFooter}>
                        {!hideCloseButton && (
                            <button className={styles.wizardCancelButton} onClick={onClose} disabled={loading} data-testid="qualification-cancel-button">
                                Cancel
                            </button>
                        )}
                        <button
                            className={styles.wizardSaveButton}
                            onClick={onSubmitSkills}
                            disabled={loading || !ready}
                            data-testid="qualification-save-button"
                            aria-label="Save qualifications"
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </button>
                    </div>

                    {/* Loader Overlay */}
                    {loading && (
                        <div className={styles.wizardLoaderOverlay}>
                            <CircularProgress />
                        </div>
                    )}
                </div>
            </div>

            {/* Left-handed Info Modal */}
            <SimpleModal isOpen={isLeftHandedModal}>
                <LeftHandedModal onCloseButtonClick={() => setIsLeftHandedModal(false)} />
            </SimpleModal>
        </>
    );
}
